import React, { useState, useEffect, useContext } from "react";
import { Box, Grid, Typography, Button, Paper, Avatar, Stack, CircularProgress } from "@mui/material";
import Slide from '@mui/material/Slide';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import PeopleIcon from '@mui/icons-material/People';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import FlashOnIcon from '@mui/icons-material/FlashOn';
import ChatBubbleIcon from '@mui/icons-material/ChatBubble';
import StarIcon from '@mui/icons-material/Star';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import { AuthContext } from "../components/Auth";
import { collection, getDocs, doc, getDoc, query, orderBy } from 'firebase/firestore';

const Hero = ({ onStartHref = "/sign-in", onExploreHref = "/channels" }) => (
    <Box sx={{ py: 10, px: 3, textAlign: 'center', background: 'linear-gradient(to bottom right, #5a6df9, #bf2fea)', color: '#fff', position: 'relative', overflow: 'hidden' }}>
        <Box sx={{ position: 'absolute', right: -120, top: -40, width: 420, height: 420, background: 'radial-gradient(circle at 20% 20%, rgba(90,109,249,0.12), transparent 30%)', transform: 'rotate(20deg)' }} />
        <Box sx={{ position: 'absolute', left: -160, bottom: -60, width: 520, height: 520, background: 'radial-gradient(circle at 80% 80%, rgba(191,47,234,0.06), transparent 30%)' }} />
        <Typography
            variant="h2"
            component="h1"
            sx={{
                fontWeight: 800,
                mb: 2,
                letterSpacing: '-0.02em',
                fontSize: { xs: '1.4rem', sm: '2.25rem', md: '3rem' },
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
            }}
        >
            Go Live. Get Seen. Get Paid.
        </Typography>
        <Typography variant="h6" sx={{ maxWidth: 820, mx: 'auto', mb: 4, color: 'rgba(255,255,255,0.9)' }}>
            A creator-first live streaming platform where anyone can start a channel, stream instantly, chat with viewers, and earn tips — all in one simple, fast experience.
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'center' }}>
            <Button variant="contained" href={onStartHref} sx={{ px: 5, py: 1.5, bgcolor: '#ffd166', color: '#071026', fontWeight: 700, boxShadow: '0 6px 18px rgba(0,0,0,0.35)' }}>
                Start Streaming
            </Button>
            <Button variant="outlined" href={onExploreHref} sx={{ px: 4, borderColor: 'rgba(255,255,255,0.12)', color: 'rgba(255,255,255,0.9)' }}>
                Explore Channels
            </Button>
        </Box>
        <Box sx={{ mt: 4, display: 'flex', gap: 2, justifyContent: 'center', color: 'rgba(255,255,255,0.65)' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><StarIcon sx={{ color: '#ffd166' }} /> Creator‑First</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><FlashOnIcon sx={{ color: '#7ee7c7' }} /> Instant Streaming</Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}><ChatBubbleIcon sx={{ color: '#8ec5ff' }} /> Live Chat</Box>
        </Box>
    </Box>
);

const CoverPage = () => {

    return (
        <>
            <Hero />
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, textAlign: 'center' }}>
                            <LiveTvIcon sx={{ fontSize: 40, color: '#5a6df9' }} />
                            <Typography variant="h6" sx={{ mt: 1 }}>Create Your Channel</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Set up your profile and go live in seconds — no complicated setup, no gatekeeping.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, textAlign: 'center' }}>
                            <FlashOnIcon sx={{ fontSize: 40, color: '#bf2fea' }} />
                            <Typography variant="h6" sx={{ mt: 1 }}>Stream Anything You Love</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Gaming, music, lifestyle, or just hanging out. If it’s real, it belongs here.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, textAlign: 'center' }}>
                            <PeopleIcon sx={{ fontSize: 40, color: '#7ee7c7' }} />
                            <Typography variant="h6" sx={{ mt: 1 }}>Build Your Community</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Viewers chat publicly, follow your channel, and come back for more.
                            </Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 2, border: '1px solid #ddd', borderRadius: 2, textAlign: 'center' }}>
                            <AttachMoneyIcon sx={{ fontSize: 40, color: '#ffd166' }} />
                            <Typography variant="h6" sx={{ mt: 1 }}>Earn Tips Instantly</Typography>
                            <Typography variant="body2" sx={{ mt: 1 }}>
                                Fans support you directly with real‑time tipping. No middlemen, no delays.
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </Box>
            <Box sx={{ flexGrow: 1, p: 2 }}>
                <Typography variant="h5" sx={{ mb: 3, fontWeight: 800, letterSpacing: '-0.01em', textAlign: 'center' }}>Why GGClive</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: 'background.paper', boxShadow: '0 6px 20px rgba(12,24,60,0.04)', transition: 'transform .18s ease, box-shadow .18s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(12,24,60,0.08)' } }}>
                            <Box sx={{ width: 64, height: 64, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'rgba(90,109,249,0.08)', mb: 1 }}>
                                <StarIcon sx={{ color: '#5a6df9', fontSize: 28 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Creator‑First</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>You own your audience, your content, and your revenue.</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: 'background.paper', boxShadow: '0 6px 20px rgba(12,24,60,0.04)', transition: 'transform .18s ease, box-shadow .18s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(12,24,60,0.08)' } }}>
                            <Box sx={{ width: 64, height: 64, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'rgba(191,47,234,0.06)', mb: 1 }}>
                                <FlashOnIcon sx={{ color: '#bf2fea', fontSize: 28 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Simple & Fast</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>A clean interface built for creators who want to stream — not manage settings.</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: 'background.paper', boxShadow: '0 6px 20px rgba(12,24,60,0.04)', transition: 'transform .18s ease, box-shadow .18s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(12,24,60,0.08)' } }}>
                            <Box sx={{ width: 64, height: 64, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'rgba(126,231,199,0.06)', mb: 1 }}>
                                <ChatBubbleIcon sx={{ color: '#7ee7c7', fontSize: 28 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Real Connection</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>Live chat brings creators and viewers together in the moment.</Typography>
                        </Box>
                    </Grid>
                    <Grid size={{ xs: 12, md: 3 }}>
                        <Box sx={{ p: 3, borderRadius: 3, textAlign: 'center', bgcolor: 'background.paper', boxShadow: '0 6px 20px rgba(12,24,60,0.04)', transition: 'transform .18s ease, box-shadow .18s ease', '&:hover': { transform: 'translateY(-6px)', boxShadow: '0 18px 40px rgba(12,24,60,0.08)' } }}>
                            <Box sx={{ width: 64, height: 64, mx: 'auto', display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '50%', bgcolor: 'rgba(255,209,102,0.06)', mb: 1 }}>
                                <PeopleIcon sx={{ color: '#ffd166', fontSize: 28 }} />
                            </Box>
                            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>Open to Everyone</Typography>
                            <Typography variant="body2" sx={{ mt: 1, color: 'text.secondary' }}>No algorithms deciding who gets seen. If you go live, you’re live.</Typography>
                        </Box>
                    </Grid>
                </Grid>

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, textAlign: 'center' }}>Featured Channels</Typography>
                    <FeaturedChannels />

                <Typography variant="h6" sx={{ mb: 1, fontWeight: 700, textAlign: 'center' }}>What Creators Say</Typography>
                <Grid container spacing={2} sx={{ mb: 3 }}>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Avatar sx={{ bgcolor: '#5a6df9' }}>AL</Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>“Finally, a streaming platform that doesn’t get in the way of streaming.”</Typography>
                                <Typography variant="caption" color="text.secondary">— Alex, creator</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Paper sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                            <Avatar sx={{ bgcolor: '#bf2fea' }}>MJ</Avatar>
                            <Box>
                                <Typography variant="body2" sx={{ fontStyle: 'italic' }}>“I made my first tip on day one.”</Typography>
                                <Typography variant="caption" color="text.secondary">— Maya, creator</Typography>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>

                <Box sx={{ textAlign: 'center', mb: 4 }}>
                    <Typography variant="h6" sx={{ fontWeight: 800, mb: 2, textAlign: 'center' }}>Join the Next Wave of Live Streaming</Typography>
                    <Typography variant="body1" sx={{ mb: 2, color: 'text.secondary' }}>Start your channel today and go live in seconds.</Typography>
                    <Button variant="contained" href="/sign-in" sx={{ px: 6, py: 1.5, bgcolor: '#ffd166', color: '#071026', fontWeight: 800, boxShadow: '0 8px 30px rgba(255,209,102,0.14)' }}>Start Streaming</Button>
                </Box>

                {/* Optional homepage variations removed per request */}
            </Box>
        </>
    );
};

export default CoverPage;

// FeaturedChannels component: desktop shows horizontal cards, mobile uses Slide carousel
function FeaturedChannels() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const { firestoreInstance } = useContext(AuthContext);
    const [slides, setSlides] = useState([]);
    const [index, setIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    function formatTimestamp(ts) {
        if (!ts) return '';
        if (ts.toDate) {
            try { return ts.toDate().toLocaleString(); } catch { return '' }
        }
        if (ts.seconds) {
            try { return new Date(ts.seconds * 1000).toLocaleString(); } catch { return '' }
        }
        if (ts._seconds) {
            try { return new Date(ts._seconds * 1000).toLocaleString(); } catch { return '' }
        }
        if (typeof ts === 'string') return ts;
        return '';
    }

    useEffect(() => {
        if (!firestoreInstance) return;

        const fetchFeatured = async () => {
            setLoading(true);
            setError(null);
            try {
                const q = query(collection(firestoreInstance, 'featured_channels'));
                const featuredSnap = await getDocs(q);
                const items = [];
                for (const fdoc of featuredSnap.docs) {
                    const fdata = fdoc.data() || {};
                    const channelId = fdata.channel_id || fdoc.id;
                    if (!channelId) continue;
                    const channelRef = doc(firestoreInstance, `channels/${channelId}`);
                    const chanSnap = await getDoc(channelRef);
                    if (!chanSnap.exists()) continue;
                    const data = chanSnap.data();
                    const creatorInfo = data && data.creator_info ? {
                        displayName: data.creator_info.displayName,
                        profileImageUrl: data.creator_info.profileImageUrl,
                        email: data.creator_info.email
                    } : {
                        displayName: data.displayName || data.title,
                        profileImageUrl: data.profileImageUrl,
                        email: data.email
                    };
                    const createdAt = data.created_at ? formatTimestamp(data.created_at) : '';
                    items.push({
                        id: channelId,
                        title: data.title || data.displayName || 'Untitled Channel',
                        bg: data.image_url || data.profileImageUrl || creatorInfo.profileImageUrl || '',
                        viewers: data.viewersCount || '--',
                        description: data.description || '',
                        createdAt,
                        followersCount: data.followersCount || 0,
                        creator: creatorInfo
                    });
                }
                setSlides(items);
            } catch (e) {
                console.error('Failed to load featured channels', e);
                setError('Failed to load featured channels');
            } finally {
                setLoading(false);
            }
        }

        fetchFeatured();
    }, [firestoreInstance]);

    useEffect(() => {
        if (!isMobile) return;
        const id = setInterval(() => setIndex(i => (i + 1) % Math.max(1, slides.length)), 4000);
        return () => clearInterval(id);
    }, [isMobile, slides.length]);

    if (loading) return (<Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>);
    if (error) return (<Box sx={{ textAlign: 'center', py: 3 }}><Typography color="error">{error}</Typography></Box>);

    if (!isMobile) {
        return (
            <Box sx={{ display: 'flex', gap: 2, overflowX: 'auto', mb: 3, pb: 1, '&::-webkit-scrollbar': { display: 'none' } }}>
                {slides.length ? slides.map((s, i) => (
                    <Paper key={i} component="a" href={`/channels/${s.id}`} sx={{ minWidth: 320, p: 1.5, borderRadius: 2, textDecoration: 'none', color: 'inherit' }}>
                        <Box sx={{ height: 160, borderRadius: 1, backgroundImage: s.bg && s.bg.startsWith('http') ? `url(${s.bg})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', bgcolor: !s.bg.startsWith || s.bg === 'grey.800' ? 'grey.800' : undefined }} />
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                                <Avatar src={s.creator?.profileImageUrl || s.bg} alt={s.creator?.displayName || s.creator?.email || s.title} sx={{ width: 36, height: 36 }}>
                                    {(!s.creator?.profileImageUrl && !s.bg) ? (s.creator?.displayName ? s.creator.displayName.charAt(0) : (s.title ? s.title.charAt(0) : null)) : null}
                                </Avatar>
                            <Box>
                                <Typography sx={{ fontWeight: 700 }}>{s.title}</Typography>
                                <Typography variant="caption" color="text.secondary">{s.creator?.displayName || s.creator?.email || ''} · {s.followersCount} followers · {s.createdAt || 'Live now'}</Typography>
                            </Box>
                        </Box>
                        {s.description && <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>{s.description}</Typography>}
                    </Paper>
                )) : (
                    <Typography color="text.secondary">No featured channels yet.</Typography>
                )}
            </Box>
        );
    }

    return (
        <Box sx={{ position: 'relative', mb: 3 }}>
            <Box sx={{ height: { xs: 180 }, position: 'relative' }}>
                {slides.length ? slides.map((s, i) => (
                    <Slide key={s.id || i} direction="left" in={index === i} mountOnEnter unmountOnExit>
                        <Paper component="a" href={`/channels/${s.id}`} sx={{ width: '100%', height: { xs: 180 }, borderRadius: 3, overflow: 'hidden', position: 'absolute', left: 0, right: 0, textDecoration: 'none', color: 'inherit' }}>
                            <Box sx={{ width: '100%', height: '100%', backgroundImage: s.bg && s.bg.startsWith('http') ? `url(${s.bg})` : 'none', backgroundSize: 'cover', backgroundPosition: 'center', bgcolor: 'grey.800' }} />
                            <Box sx={{ position: 'absolute', left: 20, bottom: 18, color: '#fff', fontWeight: 800, fontSize: '1.15rem', display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar src={s.creator?.profileImageUrl || s.bg} alt={s.creator?.displayName || s.creator?.email || s.title} sx={{ width: 36, height: 36 }}>
                                    {(!s.creator?.profileImageUrl && !s.bg) ? (s.creator?.displayName ? s.creator.displayName.charAt(0) : (s.title ? s.title.charAt(0) : null)) : null}
                                </Avatar>
                                <Box>
                                    <Typography sx={{ fontWeight: 800, color: '#fff' }}>{s.title}</Typography>
                                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)' }}>{s.creator?.displayName || s.creator?.email || ''} · {s.followersCount} followers</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Slide>
                )) : (
                    <Paper sx={{ width: '100%', height: { xs: 180 }, borderRadius: 3 }}>
                        <Box sx={{ width: '100%', height: '100%', bgcolor: 'grey.800' }} />
                    </Paper>
                )}
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 1, mt: 1 }}>
                {(slides.length ? slides : [{}, {}, {}]).map((_, i) => (
                    <Box
                        key={i}
                        onClick={() => setIndex(i)}
                        sx={{ width: i === index ? 10 : 8, height: i === index ? 10 : 8, bgcolor: '#fff', borderRadius: '50%', opacity: i === index ? 0.95 : 0.45, cursor: 'pointer' }}
                    />
                ))}
            </Box>
        </Box>
    );
}